import React, { useState, useEffect, useCallback } from "react";
import { Plus } from "lucide-react";
import SupplementPlanCreatePopup, {
  SupplementPlanData,
} from "../Supplement_Creation/SupplementPlanCreatePopup";
import { useToast, ToastContainer } from "../../Ui_Components/ToastMessage";
import { getAllSupplements } from "../../../services/subPlans";
import { useAuth } from "../../../context/auth/AuthContext";
import PermissionGate from "../../Auth/PermissionGate";
import { Button } from "../../Ui_Components/Button";

interface SupplementsPlanTabProps {
  userId: number;
  isDeactivated?: boolean;
}

interface Supplement {
  id: number;
  name: string;
  created_by: string;
  created_on: string;
  status: "Active" | "Approval Pending" | "Draft" | "In-Active";
}

const shimmerStyles = `
  @keyframes shimmer {
    0% { background-position: -1000px 0; }
    100% { background-position: 1000px 0; }
  }
  .shimmer {
    animation: shimmer 2s infinite;
    background: linear-gradient(
      to right,
      #f0f0f0 0%,
      #e0e0e0 20%,
      #f0f0f0 40%,
      #f0f0f0 100%
    );
    background-size: 1000px 100%;
  }
`;

const mapStatus = (
  code: string | number
): "Active" | "Approval Pending" | "Draft" | "In-Active" => {
  switch (String(code)) {
    case "6": return "Approval Pending";
    case "2": return "Active";
    case "3": return "In-Active";
    default:  return "Draft";
  }
};

const formatDate = (raw: string): string => {
  try {
    return new Date(raw).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return raw;
  }
};

const parseTime = (
  timeStr: string
): { consumeHour: string; consumeMinute: string; consumePeriod: string } => {
  const match = timeStr?.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return { consumeHour: "", consumeMinute: "", consumePeriod: "" };
  return {
    consumeHour: match[1].padStart(2, "0"),
    consumeMinute: match[2],
    consumePeriod: match[3].toUpperCase(),
  };
};

// Normalize frequency string from API to match dropdown option values
const normalizeFrequency = (raw: string): string => {
  if (!raw) return "";
  const trimmed = raw.trim();
  if (/^alternate/i.test(trimmed)) return "Alternate day";
  if (/^weekly/i.test(trimmed)) return "Weekly";
  if (/^daily/i.test(trimmed)) return "Daily";
  // Fallback: capitalize first letter, lowercase rest
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
};

// Normalize weekly_day string from API to match dayOfWeekOptions values (title case)
const normalizeDay = (raw: string | null | undefined): string => {
  if (!raw) return "";
  const trimmed = raw.trim();
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
};

interface ApiDetail {
  detail_id: number;
  meal_type: string;
  before_after_meal: string;
  dosage: string;
  dosage_type: string;
  status: string;
  minutes_before_after_meal: string;
}

interface ApiSupplementPlan {
  supplement_id: number;
  supplement_name: string;
  salt_name: string;
  created_by: string;
  created_date: string;
  strength: string;
  frequency: string;
  weekly_day?: string | null;
  consume_with: string;
  details: ApiDetail[];
  supplement_status: string;
}

interface ApiResponse {
  success: string;
  message: string;
  user_id: number;
  supplement_plans: ApiSupplementPlan[];
}

const toSupplementPlanData = (
  plan: ApiSupplementPlan,
  formattedDate: string,
  status: Supplement["status"]
): SupplementPlanData => ({
  id: plan.supplement_id,
  name: plan.supplement_name,
  created_by: plan.created_by,
  created_on: formattedDate,
  status,
  supplements: [
    {
      id: String(plan.supplement_id),
      name: plan.supplement_name,
      saltName: plan.salt_name,
      strength: plan.strength,
      frequency: normalizeFrequency(plan.frequency),
      dayOfWeek: normalizeDay(plan.weekly_day),
      consumeWith: plan.consume_with,
      dosages: plan.details.map((d) => {
        const { consumeHour, consumeMinute, consumePeriod } = parseTime(
          d.minutes_before_after_meal
        );
        return {
          id: String(d.detail_id),
          mealTime: d.meal_type,
          whenToTake: d.before_after_meal,
          supplementType: d.dosage_type,
          dosage: d.dosage,
          consumeHour,
          consumeMinute,
          consumePeriod,
        };
      }),
    },
  ],
});

const SupplementsPlanTab: React.FC<SupplementsPlanTabProps> = ({ userId, isDeactivated = false }) => {
  const { user } = useAuth();
  const nutristName = [user?.nutrist_fname, user?.nutrist_lname]
    .filter(Boolean)
    .join(" ");

  const [supplements, setSupplements] = useState<Supplement[]>([]);
  const [planDetailsMap, setPlanDetailsMap] = useState<Record<number, SupplementPlanData>>({});
  const [loading, setLoading] = useState(true);
  const [isCreatePopupOpen, setIsCreatePopupOpen] = useState(false);
  const [isViewPopupOpen, setIsViewPopupOpen] = useState(false);
  const [selectedPlanData, setSelectedPlanData] = useState<SupplementPlanData | null>(null);

  const { toasts, removeToast, success, error, warning, info } = useToast();

  const fetchSupplements = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const json: ApiResponse = await getAllSupplements(userId);

      if (json.success === "1" && Array.isArray(json.supplement_plans)) {
        const listItems: Supplement[] = [];
        const detailsMap: Record<number, SupplementPlanData> = {};

        json.supplement_plans.forEach((plan) => {
          const status = mapStatus(plan.supplement_status);
          const formattedDate = formatDate(plan.created_date);

          const createdByDisplay =
            plan.created_by && nutristName &&
            plan.created_by.toLowerCase().startsWith(nutristName.toLowerCase())
              ? "You"
              : plan.created_by ?? "—";

          listItems.push({
            id: plan.supplement_id,
            name: plan.supplement_name,
            created_by: createdByDisplay,
            created_on: formattedDate,
            status,
          });

          detailsMap[plan.supplement_id] = toSupplementPlanData(plan, formattedDate, status);
        });

        setSupplements(listItems);
        setPlanDetailsMap(detailsMap);
      } else {
        setSupplements([]);
        setPlanDetailsMap({});
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load supplements";
      error("Error", message);
      setSupplements([]);
      setPlanDetailsMap({});
    } finally {
      setLoading(false);
    }
  }, [userId, nutristName]);

  useEffect(() => {
    fetchSupplements();
  }, [fetchSupplements]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Active":           return "bg-[#EBF7F4] text-[#0DB5B5]";
      case "Approval Pending": return "bg-[#FFF3DE] text-[#FF9500]";
      case "Draft":            return "bg-[#E9E9E9] text-[#202020]";
      case "In-Active":        return "bg-[#FFDADA] text-[#FF3B30]";
      default:                 return "bg-gray-100 text-gray-600";
    }
  };

  const handleRowClick = (supplement: Supplement) => {
    const fullData: SupplementPlanData = planDetailsMap[supplement.id] ?? {
      id: supplement.id,
      name: supplement.name,
      created_by: supplement.created_by,
      created_on: supplement.created_on,
      status: supplement.status,
      supplements: [],
    };
    setSelectedPlanData(fullData);
    setIsViewPopupOpen(true);
  };

  const handlePlanSubmit = (_newSupplement: any) => {
    fetchSupplements();
  };

  const showToast = (
    type: "success" | "error" | "warning" | "info",
    title: string,
    message?: string,
    duration?: number
  ) => {
    switch (type) {
      case "success": success(title, message, duration); break;
      case "error":   error(title, message, duration);   break;
      case "warning": warning(title, message, duration); break;
      case "info":    info(title, message, duration);    break;
    }
  };

  return (
    <>
      <style>{shimmerStyles}</style>
      <ToastContainer toasts={toasts} onClose={removeToast} />

      <div className="h-full flex flex-col bg-white rounded-md border border-[#F3F4F6] overflow-hidden">
        {/* Fixed Header */}
        <div className="flex-shrink-0 px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold font-inter text-[#1F1F1F]">
              Supplements plan
            </h2>
            <PermissionGate resource="supplement_plan" action="create">
              <Button
                variant="primary"
                onClick={() => !isDeactivated && setIsCreatePopupOpen(true)}
                disabled={isDeactivated}
                startIcon={<Plus className="w-4 h-4" />}
              >
                Create
              </Button>
            </PermissionGate>
          </div>
        </div>

        {/* Scrollable Table Container */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {/* Fixed Table Header */}
          <div className="flex-shrink-0 overflow-x-auto bg-white border-b border-[#111C2D33] custom-scrollbar-overlay">
            <table className="w-full min-w-[800px] table-fixed">
              <thead>
                <tr className="bg-white">
                  <th className="text-left pt-2 pb-3 px-6 text-[#646464] text-sm 3xl:text-base font-medium font-inter tracking-wider w-[8%]">
                    Sl
                  </th>
                  <th className="text-left pt-2 pb-3 px-6 text-[#646464] text-sm 3xl:text-base font-medium font-inter tracking-wider w-[26%]">
                    Name
                  </th>
                  <th className="text-left pt-2 pb-3 px-6 text-[#646464] text-sm 3xl:text-base font-medium font-inter tracking-wider w-[20%]">
                    Created By
                  </th>
                  <th className="text-left pt-2 pb-3 px-6 text-[#646464] text-sm 3xl:text-base font-medium font-inter tracking-wider w-[20%]">
                    Created On
                  </th>
                  <th className="text-left pt-2 pb-3 px-6 text-[#646464] text-sm 3xl:text-base font-medium font-inter tracking-wider w-[20%]">
                    Status
                  </th>
                </tr>
              </thead>
            </table>
          </div>

          {/* Scrollable Table Body */}
          <div className="flex-1 overflow-x-auto overflow-y-auto custom-scrollbar-overlay">
            {loading ? (
              <div className="h-full shimmer" />
            ) : (
              <>
                <table className="w-full min-w-[800px] table-fixed">
                  <tbody className="bg-white divide-y divide-[#F3F4F6]">
                    {supplements.map((supplement, index) => (
                      <tr
                        key={supplement.id}
                        onClick={() => handleRowClick(supplement)}
                        className="hover:bg-[#F9FEFE] transition-colors cursor-pointer"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F] w-[8%]">
                          {String(index + 1).padStart(2, "0")}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F] w-[26%]">
                          {supplement.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F] w-[20%]">
                          {supplement.created_by}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F] w-[20%]">
                          {supplement.created_on}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap w-[20%]">
                          <span
                            className={`px-3 py-1 rounded-[24px] text-xs xl:text-sm font-medium ${getStatusColor(supplement.status)}`}
                          >
                            {supplement.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {supplements.length === 0 && (
                  <div className="px-6 py-12 text-center">
                    <p className="text-sm text-gray-500">No supplement plans found</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Create Supplement Plan Popup — guarded for deactivated clients */}
      {!isDeactivated && (
        <SupplementPlanCreatePopup
          isOpen={isCreatePopupOpen}
          onClose={() => setIsCreatePopupOpen(false)}
          onSubmit={handlePlanSubmit}
          userId={userId}
          onShowToast={showToast}
        />
      )}

      {/* View Supplement Plan Popup — always available */}
      <SupplementPlanCreatePopup
        isOpen={isViewPopupOpen}
        onClose={() => {
          setIsViewPopupOpen(false);
          setSelectedPlanData(null);
        }}
        onSubmit={() => {}}
        userId={userId}
        viewMode={true}
        initialData={selectedPlanData}
      />
    </>
  );
};

export default SupplementsPlanTab;